/**
 * WelcomeModal — one-time onboarding pop-up.
 *
 * Shown on first dashboard load after sign-up so new users know:
 *  1. What the app does (GPS · scoring · fourball · swing lab · courses)
 *  2. That problems should be reported via the in-app support form
 *     (private message) rather than as 1-star App Store reviews.
 *
 * Dismissal is sticky per device (localStorage). Users can re-open it
 * from Profile if they want a refresher.
 *
 * Keep the markup self-contained — this is a "marketing" surface that
 * may evolve quickly and we don't want it tangled into AppShell logic.
 */
import { Link } from "react-router-dom";
import {
  MapTrifold, Trophy, UsersThree, Barbell, Flag, EnvelopeSimple, X,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const STORAGE_KEY = "fairwayWelcomeSeenV1";

/** Has this device already dismissed the welcome modal? */
export function hasSeenWelcome() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; }
  catch { return true; /* if localStorage is broken, suppress the modal */ }
}

/** Force-show the welcome again (used by the "Show welcome" link in Profile). */
export function resetWelcome() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* no-op */ }
}

export default function WelcomeModal({ onClose }) {
  const { user } = useAuth();
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "golfer";

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_e) { /* no-op */ }
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
      data-testid="welcome-modal"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="bg-[var(--bg-card)] rounded-3xl w-full sm:max-w-md shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Hero band */}
        <div className="relative bg-[var(--primary)] text-white px-5 pt-5 pb-7">
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close welcome"
            data-testid="welcome-modal-close"
            className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X size={14} weight="bold" />
          </button>
          <div className="inline-flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2">
            <Flag size={11} weight="fill" /> Welcome aboard
          </div>
          <h2
            id="welcome-modal-title"
            className="font-heading font-black text-2xl leading-tight"
          >
            Hey {firstName} — welcome to Fairway.
          </h2>
          <p className="text-sm opacity-90 mt-1 leading-snug">
            Your pocket caddie for the UK, Spain &amp; Portugal — built by a
            golfer, for golfers.
          </p>
        </div>

        {/* What it does */}
        <div className="px-5 py-4 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)] mb-2">
            What you can do here
          </div>
          <ul className="space-y-2.5">
            <FeatureRow
              icon={<MapTrifold size={18} weight="fill" />}
              title="Live GPS distances"
              body="Yardage to pin on every hole, with a satellite map and a breadcrumb trail of your shots."
            />
            <FeatureRow
              icon={<Trophy size={18} weight="fill" />}
              title="Stableford &amp; Match Play"
              body="Gross or Net scoring with proper WHS handicap allocation — net leaderboards work out of the box."
            />
            <FeatureRow
              icon={<UsersThree size={18} weight="fill" />}
              title="Fourball &amp; leagues"
              body="Pass-the-phone fourballs or invite mates by link, plus season-long league tables that auto-update."
            />
            <FeatureRow
              icon={<Barbell size={18} weight="fill" />}
              title="Swing Lab (Pro)"
              body="Record a swing, get an AI pose-skeleton overlay and tempo / fault feedback on-device."
            />
            <FeatureRow
              icon={<Flag size={18} weight="fill" />}
              title="3,500+ courses, crowd-verified"
              body="Confirm pins, fix yardages and submit cards — every player makes the data better for everyone."
            />
          </ul>

          {/* Support deflection — the bit that saves bad reviews */}
          <div
            className="mt-4 rounded-2xl border-2 border-[var(--accent-terracotta)]/25 bg-[var(--accent-terracotta)]/10 p-3"
            data-testid="welcome-modal-support-cta"
          >
            <div className="flex items-start gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-[var(--accent-terracotta)] text-white flex items-center justify-center shrink-0">
                <EnvelopeSimple size={18} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-black text-[var(--primary)] text-sm leading-tight">
                  Hit a snag? Tell me first.
                </div>
                <p className="text-[12px] text-[var(--text)] mt-0.5 leading-snug">
                  Bug, missing course, weird score? Drop me a quick message
                  from inside the app and I&apos;ll get back to you personally —
                  way faster than a Play Store / App Store review.
                </p>
                <Link
                  to="/app/profile?contact=1"
                  onClick={dismiss}
                  data-testid="welcome-modal-contact-link"
                  className="inline-flex items-center gap-1.5 mt-2 text-[12px] font-bold text-[var(--accent-terracotta)] hover:underline"
                >
                  <EnvelopeSimple size={13} weight="bold" />
                  Open the contact form
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-[var(--border-default)] bg-[var(--bg-card)] flex items-center gap-2">
          <button
            type="button"
            onClick={dismiss}
            data-testid="welcome-modal-got-it"
            className="btn-primary flex-1"
          >
            Got it — let&apos;s play
          </button>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, title, body }) {
  return (
    <li className="flex items-start gap-2.5">
      <div className="h-9 w-9 rounded-xl bg-[var(--primary)]/10 text-[var(--primary)] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-[var(--primary)] text-sm leading-tight">
          {title}
        </div>
        <p className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-snug">
          {body}
        </p>
      </div>
    </li>
  );
}
